import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Package, Heart, ShoppingCart, Minus, Plus, ArrowLeft, Truck, Shield, CheckCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "218915224966";

const ProductDetailPage = () => {
  const { id } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const { addItem } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        
        // Fetch related products
        if (response.data.category_id) {
          const relatedRes = await axios.get(`${API}/products`, {
            params: { category_id: response.data.category_id },
          });
          setRelatedProducts(relatedRes.data.filter((p) => p.id !== id).slice(0, 4));
        }
        
        // Check if favorite
        if (user) {
          try {
            const favRes = await axios.get(`${API}/favorites`, {
              headers: getAuthHeaders(),
            });
            setIsFavorite(favRes.data.some((p) => p.id === id));
          } catch (e) {
            console.error("Failed to check favorites");
          }
        }
      } catch (error) {
        console.error("Failed to fetch product:", error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, user, getAuthHeaders]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.error("Please login to add favorites");
      return;
    }
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, {
          headers: getAuthHeaders(),
        });
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, {
          headers: getAuthHeaders(),
        });
        setIsFavorite(true);
        toast.success("Added to favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      toast.success(`${quantity}x ${product.name} added to cart`);
    }
  };

  const getWhatsAppLink = () => {
    const message = encodeURIComponent(
      `Hi, I'm interested in: ${product?.name}\nPrice: ${product?.sale_price} LYD`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted animate-pulse rounded-2xl" />
            <div className="space-y-6">
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              <div className="h-12 w-3/4 bg-muted animate-pulse rounded" />
              <div className="h-24 bg-muted animate-pulse rounded" />
              <div className="h-12 w-48 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-24 text-center">
          <Package className="w-24 h-24 mx-auto text-muted-foreground mb-6" />
          <h1 className="font-heading text-3xl font-bold mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/products">
            <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90">
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to Products
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const finalPrice = product.discount
    ? product.sale_price * (1 - product.discount / 100)
    : product.sale_price;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-[#f8fafc] border-b py-4">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-foreground">Products</Link>
            <span>/</span>
            <span className="text-foreground">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12" data-testid="product-detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl overflow-hidden relative"
            >
              {product.images?.[selectedImage] ? (
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-32 h-32 text-slate-300" />
                </div>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.discount > 0 && (
                  <Badge className="bg-red-500 text-white text-lg px-3 py-1">
                    -{product.discount}%
                  </Badge>
                )}
                {product.is_featured && (
                  <Badge className="bg-[#d4af37] text-[#0f172a]">Featured</Badge>
                )}
              </div>
            </motion.div>
            
            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? "border-[#1a365d]" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-[#d4af37] font-medium uppercase tracking-wide mb-2">
                {product.brand || "Bahr Al Rimal"}
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">{product.name}</h1>
              {product.name_ar && (
                <p className="text-xl text-muted-foreground">{product.name_ar}</p>
              )}
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-bold text-[#1a365d]">
                {finalPrice.toFixed(2)} LYD
              </span>
              {product.discount > 0 && (
                <span className="text-xl text-muted-foreground line-through">
                  {product.sale_price} LYD
                </span>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {product.stock_quantity > product.min_stock_alert ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-600 font-medium">In Stock</span>
                  <span className="text-muted-foreground">({product.stock_quantity} available)</span>
                </>
              ) : product.stock_quantity > 0 ? (
                <>
                  <CheckCircle className="w-5 h-5 text-orange-500" />
                  <span className="text-orange-600 font-medium">Low Stock</span>
                  <span className="text-muted-foreground">({product.stock_quantity} left)</span>
                </>
              ) : (
                <span className="text-red-600 font-medium">Out of Stock</span>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-muted-foreground leading-relaxed">{product.description}</p>
            )}

            {/* Product Details */}
            <Card>
              <CardContent className="p-4 grid grid-cols-2 gap-4 text-sm">
                {product.sku && (
                  <div>
                    <span className="text-muted-foreground">SKU:</span>
                    <span className="ml-2 font-medium">{product.sku}</span>
                  </div>
                )}
                {product.country_of_origin && (
                  <div>
                    <span className="text-muted-foreground">Origin:</span>
                    <span className="ml-2 font-medium">{product.country_of_origin}</span>
                  </div>
                )}
                {product.expiry_date && (
                  <div>
                    <span className="text-muted-foreground">Expiry:</span>
                    <span className="ml-2 font-medium">{product.expiry_date}</span>
                  </div>
                )}
                {product.batch_number && (
                  <div>
                    <span className="text-muted-foreground">Batch:</span>
                    <span className="ml-2 font-medium">{product.batch_number}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quantity & Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="decrease-qty"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                  min={1}
                  data-testid="quantity-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={quantity >= product.stock_quantity}
                  data-testid="increase-qty"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <Button
                onClick={handleAddToCart}
                disabled={product.stock_quantity === 0}
                className="flex-1 bg-[#1a365d] hover:bg-[#1a365d]/90 h-12"
                data-testid="add-to-cart"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Cart
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={toggleFavorite}
                className="h-12 w-12"
                data-testid="toggle-favorite"
              >
                <Heart className={`h-5 w-5 ${isFavorite ? "fill-red-500 text-red-500" : ""}`} />
              </Button>
            </div>

            {/* WhatsApp Order */}
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button variant="outline" className="w-full h-12 border-green-500 text-green-600 hover:bg-green-50" data-testid="whatsapp-order">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Order via WhatsApp
              </Button>
            </a>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 pt-6 border-t">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1a365d]/10 rounded-lg">
                  <Truck className="w-5 h-5 text-[#1a365d]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Fast Delivery</p>
                  <p className="text-xs text-muted-foreground">Across Libya</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#1a365d]/10 rounded-lg">
                  <Shield className="w-5 h-5 text-[#1a365d]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Quality Assured</p>
                  <p className="text-xs text-muted-foreground">Certified Products</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24">
            <h2 className="font-heading text-2xl font-bold mb-8">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((relProd) => (
                <Link key={relProd.id} to={`/products/${relProd.id}`}>
                  <Card className="group overflow-hidden hover:shadow-lg transition-all">
                    <div className="aspect-square bg-slate-100 relative">
                      {relProd.images?.[0] ? (
                        <img
                          src={relProd.images[0]}
                          alt={relProd.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium text-sm line-clamp-2 group-hover:text-[#1a365d] transition-colors">
                        {relProd.name}
                      </h3>
                      <p className="text-[#1a365d] font-bold mt-2">{relProd.sale_price} LYD</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
